import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API, formatApiError } from "../auth";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { X, ArrowLeft, ArrowRight, Camera, RotateCcw, Sparkles } from "lucide-react";

import { todayISO } from "../utils/date";
import { useFormDraft, usePhotoDraft } from "../hooks/useFormDraft";

const SKIN_TYPE = ["Normal", "Seca", "Mixta", "Grasa", "Sensible"];
const SKIN_NEEDS = ["Deshidratación", "Brillo", "Poros", "Manchas", "Rojeces", "Líneas", "Imperfecciones", "Ojeras"];
const YESNO = ["Sí", "No"];
const EYE_TECHNIQUE = ["Natural", "Soft glam", "Intensificado", "Otro"];
const LIP_TYPE = ["Labial", "Lip liner", "Gloss", "Tinta"];
const LIP_FINISH = ["Mate", "Cremoso", "Brillante"];
const LOOK_DONE = ["Natural", "Diario", "Glam", "Evento", "Otro"];

const EMPTY = {
  fecha: todayISO(),
  client_name: "",
  client_email: "",
  branch: "",
  advisor: "",
  skin_type: [],
  skin_needs: [],
  primer: [],
  primer_product: "",
  foundation_product: "",
  foundation_shade: "",
  concealer_product: "",
  concealer_shade: "",
  powder: [],
  powder_product: "",
  powder_shade: "",
  blush_product: "",
  blush_shade: "",
  contour: [],
  contour_product: "",
  contour_shade: "",
  eyeshadow_product: "",
  eyeshadow_shades: "",
  eyeliner: [],
  eyeliner_product: "",
  mascara_product: "",
  eye_technique: [],
  eye_technique_other: "",
  brow_product: "",
  brow_shade: "",
  lip_product: "",
  lip_type: [],
  lip_shade: "",
  lip_finish: [],
  look_done: [],
  look_done_other: "",
  liked_products: "",
  recommendation_1: "",
  recommendation_2: "",
  recommendation_3: "",
  observations: "",
  next_followup: "",
};

export default function NewMaquillaje() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);

  const [loadingItem, setLoadingItem] = useState(isEditing);
  const draftEnabled = !isEditing;
  const [form, setForm, { clearDraft: clearFormDraft, restored: formRestored }] = useFormDraft(
    `draft:maquillaje:new`, EMPTY, draftEnabled, { ignoreKeys: ["fecha"] }
  );
  const [photos, setPhotos, { clearDraft: clearPhotoDraft }] = usePhotoDraft(`draft:maquillaje:new:photos`, draftEnabled);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formRestored) {
      setForm((f) => (f.fecha === todayISO() ? f : { ...f, fecha: todayISO() }));
      toast.info("Borrador restaurado", {
        description: "Se recuperaron los datos que habías empezado a llenar.",
        duration: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRestored]);

  useEffect(() => {
    if (!isEditing) return;
    axios.get(`${API}/maquillajes/${editId}`, { headers: authHeaders() })
      .then(({ data }) => {
        const next = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { next[k] = data[k] ?? EMPTY[k]; });
        setForm(next);
        setPhotos((data.result_photos || []).map((p) => ({
          localId: crypto.randomUUID(), path: p, preview: `${API}/files/${p}`, uploading: false,
        })));
      })
      .catch(() => {
        toast.error("No se pudo cargar el diagnóstico");
        navigate("/maquillajes");
      })
      .finally(() => setLoadingItem(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, opt) => setForm((f) => {
    const list = f[k] || [];
    return { ...f, [k]: list.includes(opt) ? list.filter((x) => x !== opt) : [...list, opt] };
  });

  const discardDraft = () => {
    if (!window.confirm("¿Descartar el borrador y empezar de cero?")) return;
    clearFormDraft();
    clearPhotoDraft();
    setForm({ ...EMPTY, fecha: todayISO() });
    setPhotos([]);
    toast.success("Borrador descartado");
  };

  const uploadFiles = async (files) => {
    for (const file of files) {
      const localId = crypto.randomUUID();
      const preview = URL.createObjectURL(file);
      setPhotos((p) => [...p, { localId, path: null, preview, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => p.map((x) => x.localId === localId ? { ...x, path: data.path, uploading: false } : x));
      } catch (e) {
        toast.error("Error al subir foto");
        setPhotos((p) => p.filter((x) => x.localId !== localId));
      }
    }
  };
  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  };
  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.localId !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (photos.some((p) => p.uploading)) {
      toast.warning("Espera a que terminen de subir las fotos");
      return;
    }
    if (!form.client_name.trim()) {
      toast.error("El nombre de la clienta es obligatorio");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, result_photos: photos.map((p) => p.path).filter(Boolean) };
      if (isEditing) {
        await axios.put(`${API}/maquillajes/${editId}`, payload, { headers: authHeaders() });
        toast.success("Diagnóstico actualizado");
        navigate(`/maquillajes/${editId}`);
      } else {
        await axios.post(`${API}/maquillajes`, payload, { headers: authHeaders() });
        clearFormDraft();
        clearPhotoDraft();
        toast.success("Diagnóstico guardado");
        navigate("/maquillajes");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="new-maquillaje-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="mb-8">
        <div className="kbd-label mb-2 text-fuchsia-700">/ {isEditing ? "Editar diagnóstico" : "Nuevo diagnóstico"}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
          {isEditing ? "Editar diagnóstico" : "Ficha de diagnóstico"}
        </h1>
      </div>

      {loadingItem ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center kbd-label text-slate-400">Cargando…</div>
      ) : (
      <form onSubmit={submit} className="space-y-6" data-testid="maquillaje-form">
        {formRestored && !isEditing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="draft-restored-banner">
            <RotateCcw className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900 text-sm">Borrador recuperado</div>
              <div className="text-xs text-slate-600 mt-0.5">Continuaste un diagnóstico que dejaste a medias. Tus datos y fotos ya subidas se restauraron.</div>
            </div>
            <button type="button" onClick={discardDraft} className="kbd-label text-amber-800 hover:text-amber-950 underline underline-offset-4" data-testid="discard-draft-btn">
              Descartar
            </button>
          </div>
        )}

        {/* Datos de la clienta */}
        <Section title="Datos de la clienta" step="01">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Nombre de la clienta" required>
              <Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} required data-testid="field-client-name" />
            </Field>
            <Field label="Correo de la clienta">
              <Input type="email" value={form.client_email} onChange={(e) => update("client_email", e.target.value)} placeholder="clienta@correo.com" data-testid="field-client-email" />
            </Field>
            <Field label="Fecha">
              <Input type="date" value={form.fecha} readOnly disabled className="bg-slate-100 text-slate-700 cursor-not-allowed" data-testid="field-fecha" />
              <p className="text-[11px] text-slate-500 mt-1.5">Fecha de hoy (no editable)</p>
            </Field>
            <Field label="Punto de venta">
              <Input value={form.branch} onChange={(e) => update("branch", e.target.value)} data-testid="field-branch" />
            </Field>
            <Field label="Consejera">
              <Input value={form.advisor} onChange={(e) => update("advisor", e.target.value)} data-testid="field-advisor" />
            </Field>
          </div>
        </Section>

        {/* 1. Diagnóstico de piel */}
        <Section title="Diagnóstico de piel" step="02">
          <CheckGroup label="Tipo de piel" options={SKIN_TYPE} selected={form.skin_type} onToggle={(o) => toggle("skin_type", o)} testId="skin-type" />
          <div className="mt-5">
            <CheckGroup label="Necesidades" options={SKIN_NEEDS} selected={form.skin_needs} onToggle={(o) => toggle("skin_needs", o)} testId="skin-needs" />
          </div>
        </Section>

        {/* 2. Base y preparación */}
        <Section title="Base y preparación" step="03">
          <CheckGroup label="Primer" options={YESNO} selected={form.primer} onToggle={(o) => toggle("primer", o)} testId="primer" />
          <div className="mt-3 mb-6">
            <TextField label="Primer — Producto" value={form.primer_product} onChange={(v) => update("primer_product", v)} testId="primer-product" />
          </div>
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <TextField label="Base / Foundation — Producto" value={form.foundation_product} onChange={(v) => update("foundation_product", v)} testId="foundation-product" />
            <TextField label="Base — Tono" value={form.foundation_shade} onChange={(v) => update("foundation_shade", v)} testId="foundation-shade" />
            <TextField label="Corrector — Producto" value={form.concealer_product} onChange={(v) => update("concealer_product", v)} testId="concealer-product" />
            <TextField label="Corrector — Tono" value={form.concealer_shade} onChange={(v) => update("concealer_shade", v)} testId="concealer-shade" />
          </div>
          <CheckGroup label="Polvo" options={YESNO} selected={form.powder} onToggle={(o) => toggle("powder", o)} testId="powder" />
          <div className="grid md:grid-cols-2 gap-5 mt-3">
            <TextField label="Polvo — Producto" value={form.powder_product} onChange={(v) => update("powder_product", v)} testId="powder-product" />
            <TextField label="Polvo — Tono" value={form.powder_shade} onChange={(v) => update("powder_shade", v)} testId="powder-shade" />
          </div>
        </Section>

        {/* 3. Rostro */}
        <Section title="Rostro" step="04">
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <TextField label="Blush / Rubor — Producto" value={form.blush_product} onChange={(v) => update("blush_product", v)} testId="blush-product" />
            <TextField label="Blush — Tono" value={form.blush_shade} onChange={(v) => update("blush_shade", v)} testId="blush-shade" />
          </div>
          <CheckGroup label="Contorno" options={YESNO} selected={form.contour} onToggle={(o) => toggle("contour", o)} testId="contour" />
          <div className="grid md:grid-cols-2 gap-5 mt-3">
            <TextField label="Contorno — Producto" value={form.contour_product} onChange={(v) => update("contour_product", v)} testId="contour-product" />
            <TextField label="Contorno — Tono" value={form.contour_shade} onChange={(v) => update("contour_shade", v)} testId="contour-shade" />
          </div>
        </Section>

        {/* 4. Ojos */}
        <Section title="Ojos" step="05">
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <TextField label="Sombras — Producto / Paleta" value={form.eyeshadow_product} onChange={(v) => update("eyeshadow_product", v)} testId="eyeshadow-product" />
            <TextField label="Sombras — Tonos" value={form.eyeshadow_shades} onChange={(v) => update("eyeshadow_shades", v)} testId="eyeshadow-shades" />
          </div>
          <CheckGroup label="Delineador" options={YESNO} selected={form.eyeliner} onToggle={(o) => toggle("eyeliner", o)} testId="eyeliner" />
          <div className="grid md:grid-cols-2 gap-5 mt-3 mb-6">
            <TextField label="Delineador — Producto" value={form.eyeliner_product} onChange={(v) => update("eyeliner_product", v)} testId="eyeliner-product" />
            <TextField label="Máscara — Producto" value={form.mascara_product} onChange={(v) => update("mascara_product", v)} testId="mascara-product" />
          </div>
          <CheckGroup label="Técnica / estilo" options={EYE_TECHNIQUE} selected={form.eye_technique} onToggle={(o) => toggle("eye_technique", o)} testId="eye-technique" />
          {form.eye_technique.includes("Otro") && (
            <div className="mt-3">
              <TextField label="Técnica — Otro (especifica)" value={form.eye_technique_other} onChange={(v) => update("eye_technique_other", v)} testId="eye-technique-other" />
            </div>
          )}
        </Section>

        {/* 5. Cejas */}
        <Section title="Cejas" step="06">
          <div className="grid md:grid-cols-2 gap-5">
            <TextField label="Producto" value={form.brow_product} onChange={(v) => update("brow_product", v)} testId="brow-product" />
            <TextField label="Tono / Técnica" value={form.brow_shade} onChange={(v) => update("brow_shade", v)} testId="brow-shade" />
          </div>
        </Section>

        {/* 6. Labios */}
        <Section title="Labios" step="07">
          <div className="mb-6">
            <TextField label="Producto" value={form.lip_product} onChange={(v) => update("lip_product", v)} testId="lip-product" />
          </div>
          <CheckGroup label="Tipo" options={LIP_TYPE} selected={form.lip_type} onToggle={(o) => toggle("lip_type", o)} testId="lip-type" />
          <div className="mt-3 mb-6">
            <TextField label="Tono" value={form.lip_shade} onChange={(v) => update("lip_shade", v)} testId="lip-shade" />
          </div>
          <CheckGroup label="Acabado" options={LIP_FINISH} selected={form.lip_finish} onToggle={(o) => toggle("lip_finish", o)} testId="lip-finish" />
        </Section>

        {/* 7. Resultado y seguimiento */}
        <Section title="Resultado y seguimiento" step="08">
          <CheckGroup label="Look realizado" options={LOOK_DONE} selected={form.look_done} onToggle={(o) => toggle("look_done", o)} testId="look-done" />
          {form.look_done.includes("Otro") && (
            <div className="mt-3">
              <TextField label="Look realizado — Otro (especifica)" value={form.look_done_other} onChange={(v) => update("look_done_other", v)} testId="look-done-other" />
            </div>
          )}
          <div className="mt-6 mb-6">
            <TextField label="Productos / tonos de interés" value={form.liked_products} onChange={(v) => update("liked_products", v)} testId="liked-products" />
          </div>
          <Label className="kbd-label">Recomendaciones</Label>
          <div className="space-y-3 mt-2 mb-6">
            <Input value={form.recommendation_1} onChange={(e) => update("recommendation_1", e.target.value)} placeholder="1." data-testid="field-rec-1" />
            <Input value={form.recommendation_2} onChange={(e) => update("recommendation_2", e.target.value)} placeholder="2." data-testid="field-rec-2" />
            <Input value={form.recommendation_3} onChange={(e) => update("recommendation_3", e.target.value)} placeholder="3." data-testid="field-rec-3" />
          </div>
          <div className="mb-5">
            <Label className="kbd-label">Observaciones</Label>
            <Textarea rows={3} value={form.observations} onChange={(e) => update("observations", e.target.value)} className="mt-2" data-testid="field-observations" />
          </div>
          <Field label="Próximo seguimiento">
            <Input type="date" value={form.next_followup} onChange={(e) => update("next_followup", e.target.value)} data-testid="field-next-followup" />
          </Field>
        </Section>

        {/* Fotos del resultado (opcional) */}
        <Section title="Fotos del resultado / look" step="09" hint="Opcional. Puedes tomar una foto o subirla desde la galería.">
          <div className="mb-3">
            <label htmlFor="result-camera-input" className="flex flex-col items-center justify-center border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/50 hover:border-fuchsia-600 hover:bg-fuchsia-50 transition-colors rounded-lg p-6 cursor-pointer text-center" data-testid="result-camera-trigger">
              <Camera className="w-7 h-7 text-fuchsia-700 mb-2" />
              <div className="font-display font-semibold text-slate-900 text-sm">Tomar foto o subir de galería</div>
              <div className="kbd-label mt-1 !text-[9px]">Cámara o galería · opcional</div>
              <input id="result-camera-input" type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="result-camera-input" />
            </label>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((p) => (
                <div key={p.localId} className="relative aspect-square bg-slate-100 rounded-md overflow-hidden group">
                  <img src={p.preview} alt="look" className="w-full h-full object-cover" />
                  {p.uploading && <div className="absolute inset-0 bg-slate-950/50 flex items-center justify-center"><div className="kbd-label !text-white">Subiendo…</div></div>}
                  <button type="button" onClick={() => removePhoto(p.localId)} className="absolute top-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-result-${p.localId}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3 pt-4 sticky bottom-0 bg-slate-50 p-4 -mx-5 md:-mx-8 lg:-mx-12 border-t border-slate-200">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-slate-300" data-testid="cancel-btn">Cancelar</Button>
          <Button type="submit" disabled={submitting} className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white px-6 group" data-testid="submit-maquillaje-btn">
            {submitting ? (
              isEditing ? "Actualizando…" : "Guardando…"
            ) : isEditing ? (
              <>Actualizar diagnóstico <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
            ) : (
              <><Sparkles className="mr-2 w-4 h-4" /> Guardar diagnóstico</>
            )}
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}

function Section({ title, step, hint, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-7">
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-slate-100">
        <span className="kbd-label font-mono-tv bg-fuchsia-700 text-white px-2 py-1">{step}</span>
        <div>
          <h3 className="font-display text-xl font-bold text-slate-950">{title}</h3>
          {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, span, children }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <Label className="kbd-label">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="kbd-label">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-2" data-testid={`field-${testId}`} />
    </div>
  );
}

function CheckGroup({ label, options, selected, onToggle, testId }) {
  return (
    <div>
      <Label className="kbd-label">{label}</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = (selected || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`text-sm px-3.5 py-2 rounded-full border transition-colors ${active ? "bg-fuchsia-700 text-white border-fuchsia-700" : "bg-white text-slate-700 border-slate-300 hover:border-fuchsia-600"}`}
              data-testid={`opt-${testId}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
